import { Box } from '@amzn/ring-ui-react-components';
import { border } from 'polished';
import { mergeRight } from 'ramda';
import { Ref, useMemo } from 'react';
import ReactSelect, { ControlProps, Props, SelectInstance } from 'react-select';
import { useThemeStyles } from 'src/shared/hooks';
import type { Theme } from 'src/shared/theme';
import { MultiValueLabel } from './MultiValueLabel';
import { Option } from './Option';
import { BaseOptionType } from './types';
import { Icon } from '../Icon';
import type { CSSObject } from '@emotion/serialize';

const DropdownIndicator = () => (
  <Icon name="chevron-down-select" size={24} mt={3} mx={2} />
);

const withDefaultComponents = mergeRight({
  Option,
  MultiValueLabel,
  DropdownIndicator,
});

export type SelectProps<
  OptionType = BaseOptionType,
  IsMulti extends boolean = boolean,
> = Omit<
  Props<OptionType, IsMulti>,
  'closeMenuOnSelect' | 'css' | 'hideSelectedOptions' | 'styles'
> & {
  ref?: Ref<SelectInstance<OptionType, IsMulti>>;
  isInvalid?: boolean;
};

export const Select = <
  OptionType extends BaseOptionType,
  IsMulti extends boolean,
>({
  components,
  ...props
}: SelectProps<OptionType, IsMulti>) => {
  const { isMulti, isInvalid } = props;

  const styles = useThemeStyles((theme) => getSelectStyles(theme, isInvalid));

  const finalComponents = useMemo(
    () => (components ? withDefaultComponents(components) : undefined),
    [components],
  );

  return (
    <Box width="100%">
      <ReactSelect
        {...props}
        ref={props.ref}
        closeMenuOnSelect={!isMulti}
        components={finalComponents}
        hideSelectedOptions={false}
        styles={styles}
        isOptionDisabled={(option) => option?.disabled || false}
      />
    </Box>
  );
};

export const getSelectStyles = (
  { colors, fontWeights, space, shadows }: Theme,
  isInvalid?: boolean,
) => {
  const fontStyles = {
    fontSize: 14,
    fontWeight: fontWeights.regular,
  };

  return {
    control: (base: CSSObject, { isFocused }: ControlProps<any>) => ({
      ...base,
      ...(!isInvalid && {
        ...border(
          '1px',
          'solid',
          isFocused ? colors.primary[500] : colors.tertiary[700],
        ),
      }),
      ...(isInvalid && {
        ...border('1px', 'solid', colors.negative[500]),
        boxShadow: `0 0 0 1px ${colors.negative[500]}`,
        '&:hover': {
          borderColor: colors.negative[500],
        },
      }),
      minHeight: 48,
      boxShadow: isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
      '&:hover': {
        borderColor: isFocused ? colors.primary[500] : undefined,
      },
    }),
    input: (base: CSSObject) => ({
      ...base,
      ...fontStyles,
      border: 'none',
      color: colors.neutral[500],
    }),
    placeholder: (base: CSSObject) => ({
      ...base,
      ...fontStyles,
      color: colors.neutral[300],
    }),
    indicatorSeparator: (base: CSSObject) => ({
      ...base,
      display: 'none',
    }),
    valueContainer: (base: CSSObject) => ({
      ...base,
      padding: `0 ${space[3]}px`,
    }),
    singleValue: (base: CSSObject) => ({
      ...base,
      ...fontStyles,
      color: colors.neutral[500],
      marginLeft: 0,
    }),
    multiValue: (base: CSSObject) => ({
      ...base,
      backgroundColor: colors.white,
    }),
    menu: (base: CSSObject) => ({
      ...base,
      ...border('1px', 'solid', colors.tertiary[700]),
      boxShadow: shadows.header,
      overflow: 'hidden',
    }),
    menuList: (base: CSSObject) => ({
      ...base,
      padding: 0,
    }),
    loadingMessage: (base: CSSObject) => ({
      ...base,
      ...fontStyles,
      color: colors.neutral[300],
    }),
    noOptionsMessage: (base: CSSObject) => ({
      ...base,
      ...fontStyles,
      color: colors.neutral[300],
    }),
  };
};
